import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { getScrollEventTarget, getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLElement} [priceDisplay] - Price container, when the bar shows a price
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #buyButtonsInView = false;

  /** @type {boolean} */
  #footerInView = false;

  /**
   * The bar's own price markup, as the section rendered it for the selected variant.
   * Kept so the one time price can be put back when the shopper leaves the
   * subscription, and recaptured after every morph.
   * @type {string | null}
   */
  #onceOffPriceHTML = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;

    // "Scroll" mode: the bar comes in as soon as the page is scrolled down past the
    // offset and goes away again near the top, instead of waiting for the buy
    // buttons to leave the viewport. On desktop `.page-wrapper` scrolls, not the
    // window (Lenis + squeeze layout), so the listener goes on whichever element
    // is the scroll container and follows it across the breakpoint.
    if (this.#showOnScroll) {
      this.#listenToScrollContainer(signal);
      scrollContainerMediaQuery.addEventListener('change', () => this.#listenToScrollContainer(signal), { signal });
      this.#handleScroll();
    }

    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener('subscribe-save:change', this.#handlePurchaseOptionChange, { signal });

    // Bundle cards: picking one (or changing the quantity, which can select or clear one)
    // changes the price the shopper pays, so the bar re-reads it.
    target?.addEventListener('change', this.#handleBundleChange, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleBundleChange, { signal });

    this.#getInitialQuantity();
    this.#syncPurchaseOptionPrice();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (this.#showOnScroll) {
        this.#buyButtonsInView = entry.isIntersecting;
        this.#handleScroll();
        return;
      }

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (this.#showOnScroll) {
          this.#footerInView = entry.isIntersecting;
          this.#handleScroll();
          return;
        }

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /** @returns {boolean} */
  get #showOnScroll() {
    return this.dataset.showMode === 'scroll';
  }

  /** @type {AbortController | null} */
  #scrollListener = null;

  /**
   * Attaches the scroll listener to the current scroll container. Scroll events do
   * not bubble, so `window` only hears them while the document itself scrolls.
   *
   * @param {AbortSignal} signal
   */
  #listenToScrollContainer(signal) {
    this.#scrollListener?.abort();
    this.#scrollListener = new AbortController();
    signal.addEventListener('abort', () => this.#scrollListener?.abort(), { once: true });

    getScrollEventTarget().addEventListener('scroll', this.#handleScroll, {
      signal: this.#scrollListener.signal,
      passive: true,
    });
  }

  /**
   * "Scroll" mode visibility: shown once the page is scrolled down past the offset,
   * unless the buy buttons or the footer are on screen or the chat is active.
   */
  #handleScroll = () => {
    const offset = Number(this.dataset.scrollOffset) || 0;
    const shouldShow =
      getScrollTop() > offset && !this.#buyButtonsInView && !this.#footerInView && !this.#isChatActive();

    if (shouldShow && !this.#isStuck) {
      this.#showStickyBar();
    } else if (!shouldShow && this.#isStuck) {
      this.#hideStickyBar();
    }
  };

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // The morph brought this variant's one time price, so that is the new price to
        // fall back to, and the subscription price is laid over it again.
        this.#onceOffPriceHTML = null;
        this.#syncPurchaseOptionPrice();

        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /** Follows Subscribe & Save's chosen purchase option. */
  #handlePurchaseOptionChange = () => {
    this.#syncPurchaseOptionPrice();
  };

  /**
   * Follows the bundle cards. Deferred a tick so the bundle selector and the quantity
   * field have finished updating (and the selected card is marked) before the bar reads it.
   */
  #handleBundleChange = () => {
    setTimeout(() => {
      if (!this.#abortController.signal.aborted) this.#syncPurchaseOptionPrice();
    }, 0);
  };

  /**
   * The price the shopper pays for the selected bundle card, read from the card itself so
   * the bar always says what the card says. The card's total holds the struck through
   * regular price as a child element and the discounted price as its own text.
   *
   * @returns {{ price: string, comparePrice: string } | null} null when no card is selected.
   */
  #getSelectedBundlePrices() {
    const section = this.closest('.shopify-section');
    const card = section?.querySelector('.bundle-option--selected');
    const total = card?.querySelector('.bundle-option__price--total');
    if (!total) return null;

    const compareElement = total.querySelector('.bundle-option__compare-price');
    const comparePrice = compareElement?.textContent?.trim() ?? '';

    const totalCopy = /** @type {HTMLElement} */ (total.cloneNode(true));
    totalCopy.querySelector('.bundle-option__compare-price')?.remove();
    const price = totalCopy.textContent?.trim() ?? '';
    if (!price) return null;

    return { price, comparePrice: comparePrice && comparePrice !== price ? comparePrice : '' };
  }

  /**
   * Shows the price the shopper will actually pay: the subscription price while the
   * subscription is the chosen purchase option, otherwise the selected bundle card's
   * price, and the variant's own price when neither applies.
   *
   * The prices are read from the Subscribe & Save card and the bundle card rather than
   * recomputed, so the bar always says what they say - including after a frequency
   * change, which prices off a different selling plan.
   */
  #syncPurchaseOptionPrice() {
    const priceDisplay = this.refs.priceDisplay;
    if (!priceDisplay) return;

    if (this.#onceOffPriceHTML == null) this.#onceOffPriceHTML = priceDisplay.innerHTML;

    const section = this.closest('.shopify-section');
    const card = section?.querySelector('subscribe-save');
    const checked = card?.querySelector('input[type="radio"]:checked');
    const isSubscription = checked instanceof HTMLInputElement && checked.value === 'subscription';
    const subscriptionPrice = card?.querySelector('.subscribe-save__price')?.textContent?.trim();
    const checkbox = /** @type {HTMLElement | null} */ (section?.querySelector('subscription-checkbox'));
    const checkboxOn = Boolean(checkbox?.querySelector('.fcl-subscribe__input:checked'));

    let price = '';
    let comparePrice = '';

    if (isSubscription && subscriptionPrice) {
      price = subscriptionPrice;
      comparePrice = card?.querySelector('.subscribe-save__compare-price')?.textContent?.trim() ?? '';
    } else if (checkboxOn && checkbox?.dataset.todayPrice) {
      price = checkbox.dataset.todayPrice;
      comparePrice = checkbox.dataset.comparePrice ?? '';
    } else {
      const bundle = this.#getSelectedBundlePrices();
      if (bundle) ({ price, comparePrice } = bundle);
    }

    if (!price) {
      priceDisplay.innerHTML = this.#onceOffPriceHTML;
      priceDisplay.removeAttribute('data-purchase-option');
      return;
    }

    // Only the swapped in prices are laid out as a row; the section's own price markup
    // brings its own layout and is left alone. (The 'subscription' value is what the
    // bar's stylesheet lays out as a row; bundle prices reuse it.)
    priceDisplay.setAttribute('data-purchase-option', 'subscription');
    priceDisplay.replaceChildren(
      this.#priceElement('price-item__group price', price),
      ...(comparePrice ? [this.#priceElement('price-item__group price-item--regular compare-at-price', comparePrice)] : [])
    );
  }

  /**
   * @param {string} className - The price classes to carry.
   * @param {string} text - The formatted price.
   * @returns {HTMLSpanElement}
   */
  #priceElement(className, text) {
    const element = document.createElement('span');
    element.className = className;
    element.textContent = text;
    return element;
  }

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    // The bar can be told to show regardless of the Inbox chat widget.
    if (this.hasAttribute('data-ignore-chat')) return false;
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
